import * as React from 'react';
import cx from 'clsx';

import {
  countInitialValue,
  calculatePresentedValue,
  getDisplayedDecimalsAmount,
} from './utils';

import { JackpotBoxScale, JackpotState, WrapperType } from '../../types';

import styles from './JackpotNumber.module.scss';

interface JackpotNumberPropsInterface {
  currencyCode: string;
  value: number;
  valueAnimationDuration: number;
  jackpotScale: JackpotBoxScale;
  jackpotState: JackpotState;
  locale: string;
  wrapperType: WrapperType;
}

const JackpotNumber: React.FC<JackpotNumberPropsInterface> = ({
  currencyCode,
  jackpotScale,
  jackpotState,
  locale,
  wrapperType,
  value = 0,
  valueAnimationDuration,
}) => {
  const initialValue = countInitialValue(value, valueAnimationDuration);

  const [previousValue, setPreviousValue] = React.useState<number>(
    initialValue
  );
  const [presentedValue, setPresentedValue] = React.useState<number>(
    initialValue
  );

  React.useEffect(() => {
    let animationFrame = 0;
    let startTime = 0;
    const tickToValue = () => {
      if (animationFrame) {
        forceStopAnimation();
      }

      setPreviousValue(presentedValue);
      if (value < presentedValue) {
        const initialValue = countInitialValue(value, valueAnimationDuration);
        setPresentedValue(initialValue);
      }
      animationFrame = requestAnimationFrame(tickAnimationFrame);
    };

    const tickAnimationFrame = (timestamp: number) => {
      startTime = startTime || timestamp;
      const timeDifference = timestamp - startTime;
      if (timeDifference > valueAnimationDuration) {
        setPresentedValue(value);
        forceStopAnimation();
        return;
      }

      const newPresentedValue = calculatePresentedValue(
        timeDifference,
        valueAnimationDuration,
        previousValue,
        value
      );
      if (newPresentedValue.toFixed(2) !== presentedValue.toFixed(2)) {
        // Update state only if displayed value actually changes
        setPresentedValue(newPresentedValue);
      }

      animationFrame = requestAnimationFrame(tickAnimationFrame);
    };

    const forceStopAnimation = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = 0;
      startTime = 0;
    };

    tickToValue();
    return () => {
      cancelAnimationFrame(animationFrame);
    };
  }, [presentedValue, previousValue, value, valueAnimationDuration]);

  let tmpVal = 1;
  const currencySymbol = tmpVal
    .toLocaleString(locale, {
      style: 'currency',
      currency: currencyCode,
      maximumSignificantDigits: tmpVal.toString().length,
      currencyDisplay: 'symbol',
    })
    .replace(tmpVal.toString(), '');

  const displayedDecimalsAmount = getDisplayedDecimalsAmount(presentedValue);
  const numberArray = (presentedValue || 0)
    .toLocaleString(locale, {
      minimumFractionDigits: displayedDecimalsAmount,
      maximumFractionDigits: displayedDecimalsAmount,
    })
    .split('');

  const isSpecialState = ['active', 'won'].includes(jackpotState);

  return (
    <div className={styles.container}>
      <div className={styles.currency} data-testid="jackpot-currency">
        {currencySymbol}
      </div>
      {numberArray.map((digit, i) =>
        Number.isInteger(parseInt(digit, 10)) ? (
          <div
            className={cx(
              styles.box,
              jackpotScale === 'medium' && styles.boxMedium,
              styles[
                `boxBackground__${wrapperType}__${
                  isSpecialState ? 'special' : 'normal'
                }`
              ]
            )}
            data-testid="jackpot-digit-box"
            key={i}
          >
            <div
              className={cx(
                styles.digit,
                jackpotScale === 'medium' && styles.digitMedium
              )}
              data-testid="jackpot-digit"
            >
              {digit}
            </div>
          </div>
        ) : (
          <div className={styles.char} data-testid="jackpot-char" key={i}>
            {digit}
          </div>
        )
      )}
    </div>
  );
};

export default React.memo(JackpotNumber);
